import React from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { IconContext } from 'react-icons';
import './styles/global.css';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { APP_ROUTES, LEGACY_ACCOUNT_ROUTES } from './navigation/appNavigation';

// These stylesheets came in with the previous homepage, so they have always
// been global and loaded in this order. Feature pages, the dashboard and the
// editor rely on some of their rules (.section-title, .section-header,
// .feature-card, .btn-icon, .progress-track, .plan-name, .shape-1/2), so
// they stay here, unchanged, after the homepage itself moved to LandingPage.
import './components/sections/HeroSection.css';
import './components/sections/HomeSections.css';
import './components/sections/PricingSection.css';
import './components/layout/Navbar.css';
import './components/layout/Footer.css';
import LandingPage from './components/landing/LandingPage';

// Route-level code splitting keeps editor/export dependencies out of the
// marketing-page entry bundle.
const ScriptAnalysisPage = React.lazy(() => import('./pages/ScriptAnalysisPage'));
const SchedulingPage = React.lazy(() => import('./pages/SchedulingPage'));
const BudgetPage = React.lazy(() => import('./pages/BudgetPage'));
const CollaborationPage = React.lazy(() => import('./pages/CollaborationPage'));
const ExportPage = React.lazy(() => import('./pages/ExportPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const AdminAiPage = React.lazy(() => import('./pages/AdminAiPage'));
const EditorLayout = React.lazy(() => import('./components/editor/EditorLayout'));
const ProjectEditorRoute = React.lazy(() => import('./components/editor/ProjectEditorRoute'));
const ProjectInvitationPage = React.lazy(() => import('./components/project-access/ProjectInvitationPage'));
const ReferenceLibraryPage = React.lazy(() => import('./pages/ReferenceLibraryPage'));
const SharedReferencesPage = React.lazy(() => import('./pages/SharedReferencesPage'));

// Smart Homepage — redirect logged-in users to dashboard
const HomePage: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user) return <Navigate to={APP_ROUTES.projects} replace />;

  return <LandingPage />;
};

// Smart AuthPage wrapper — redirect if already logged in
const AuthPageWrapper: React.FC<{ initialMode?: 'login' | 'signup' }> = ({ initialMode }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user) return <Navigate to={APP_ROUTES.projects} replace />;

  return <AuthPage initialMode={initialMode} />;
};

export const AppRoutes: React.FC = () => (
            <Routes>
              {/* Public routes */}
              <Route path={APP_ROUTES.home} element={<HomePage />} />
              <Route path="/features/script-analysis" element={<ScriptAnalysisPage />} />
              <Route path="/features/scheduling" element={<SchedulingPage />} />
              <Route path="/features/budget" element={<BudgetPage />} />
              <Route path="/features/collaboration" element={<CollaborationPage />} />
              <Route path="/features/export" element={<ExportPage />} />
              <Route path="/features/security" element={<SecurityPage />} />
              <Route path={APP_ROUTES.signIn} element={<AuthPageWrapper />} />
              <Route path="/try-free" element={<AuthPageWrapper initialMode="signup" />} />
              <Route path="/signup" element={<AuthPageWrapper initialMode="signup" />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Keep the legacy callback path working for confirmation emails already sent. */}
              <Route path="/auth" element={<AuthPageWrapper />} />
              <Route path="/invitation/:invitationId" element={<ProjectInvitationPage />} />
              {/* Public, view-only stock reference boards shared by link. */}
              <Route path="/shared/references/:token" element={<SharedReferencesPage />} />
              <Route path="/editor/demo/*" element={<EditorLayout />} />

              {/* Protected routes */}
              <Route path={APP_ROUTES.projects} element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path={APP_ROUTES.sharedProjects} element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path={APP_ROUTES.recentlyDeleted} element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path={`${APP_ROUTES.account}/*`} element={
                <ProtectedRoute><AccountPage /></ProtectedRoute>
              } />
              <Route path={APP_ROUTES.adminAi} element={
                <ProtectedRoute><AdminAiPage /></ProtectedRoute>
              } />
              {LEGACY_ACCOUNT_ROUTES.map(path => (
                <Route key={path} path={path} element={
                  <ProtectedRoute><Navigate to={`${APP_ROUTES.account}/${path === APP_ROUTES.legacySettings ? 'preferences' : 'profile'}`} replace /></ProtectedRoute>
                } />
              ))}
              <Route path="/editor/:projectId/*" element={
                <ProtectedRoute><ProjectEditorRoute /></ProtectedRoute>
              } />
              <Route path="/references" element={
                <ProtectedRoute><ReferenceLibraryPage /></ProtectedRoute>
              } />

              {/* Never leave an unknown URL as an empty application shell. */}
              <Route path="*" element={<Navigate to={APP_ROUTES.home} replace />} />
            </Routes>
);

const App: React.FC = () => {
  // A data router supplies supported navigation blocking, including Back/Forward.
  const [router] = React.useState(() => createBrowserRouter([{ path: '*', element:
    <React.Suspense fallback={<div role="status" className="route-loading">Loading EzyPrep…</div>}><AppRoutes /></React.Suspense>,
  }]));
  return <IconContext.Provider value={{ className: 'react-icon' }}><ErrorBoundary><AuthProvider>
    <RouterProvider router={router} />
  </AuthProvider></ErrorBoundary></IconContext.Provider>;
};
export default App;
